import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import SearchBox from "../SearchBox/SearchBox";
import DatePicker from "react-datepicker";
import CardImg from "../../assests/img/card.svg";
import IpadImg from "../../assests/img/ipad.svg";
import {
  createBlog,
  deleteBlog,
  fetchBlogById,
  fetchBlogList,
  updateBlog,
  uploadBlogsJson,
} from "../../services/blogApi";
import { assertAdminAuth, getApiErrorMessage } from "../../services/apiClient";
import { getBlogImage } from "../../util/mediaHelpers";
import {
  isValidImageUrl,
  normalizeImageList,
  readFileAsDataUrl,
} from "../../util/imageHelpers";
import "./BlogAdminTab.scss";

const emptyBlogForm = {
  topic: "",
  img: "AutomationImg",
  title: "",
  desc: "",
  firstImage: "./assets/blog/blogsIOT.png",
  paraHeading: "",
  paraOne: "",
  questionOne: "",
  answereList: [{ id: "1", pointOne: "" }],
  secondImage: "./assets/blog/technology.png",
  questionTwo: "",
  answerPara: "",
  btnTitle: "Read More",
  date: "Today",
};

const BlogAdminTab = ({ labels }) => {
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogForm, setBlogForm] = useState(emptyBlogForm);
  const [blogImages, setBlogImages] = useState(
    normalizeImageList(emptyBlogForm.firstImage, emptyBlogForm.secondImage),
  );
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);
  const jsonInputRef = useRef(null);

  const loadBlogs = async () => {
    try {
      const items = await fetchBlogList();
      setBlogs(items);
    } catch (error) {
      console.error("Failed to load blogs", error);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  const filteredBlogs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return blogs.filter((blog) => {
      const matchesSearch =
        !query ||
        blog.title?.toLowerCase().includes(query) ||
        blog.topic?.toLowerCase().includes(query) ||
        blog.desc?.toLowerCase().includes(query);

      if (!matchesSearch) {
        return false;
      }

      if (!startDate && !endDate) {
        return true;
      }

      const blogDate = blog.createAt ? new Date(blog.createAt) : null;

      if (!blogDate || Number.isNaN(blogDate.getTime())) {
        return true;
      }

      if (startDate && blogDate < startDate) {
        return false;
      }

      if (endDate && blogDate > endDate) {
        return false;
      }

      return true;
    });
  }, [blogs, searchQuery, startDate, endDate]);

  const resetForm = () => {
    setBlogForm(emptyBlogForm);
    setBlogImages(
      normalizeImageList(emptyBlogForm.firstImage, emptyBlogForm.secondImage),
    );
    setImageUrlInput("");
    setEditingBlogId(null);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = async (blogId) => {
    if (!assertAdminAuth()) {
      return;
    }

    setIsSaving(true);

    try {
      const blog = await fetchBlogById(blogId);
      const nextForm = {
        topic: blog.topic || "",
        img: blog.img || "AutomationImg",
        title: blog.title || "",
        desc: blog.desc || "",
        firstImage: blog.firstImage || "./assets/blog/blogsIOT.png",
        paraHeading: blog.paraHeading || "",
        paraOne: blog.paraOne || "",
        questionOne: blog.questionOne || "",
        answereList: blog.answereList?.length
          ? blog.answereList
          : [{ id: "1", pointOne: "" }],
        secondImage: blog.secondImage || "./assets/blog/technology.png",
        questionTwo: blog.questionTwo || "",
        answerPara: blog.answerPara || "",
        btnTitle: blog.btnTitle || "Read More",
        date: blog.date || "Today",
      };

      setEditingBlogId(blogId);
      setBlogForm(nextForm);
      setBlogImages(normalizeImageList(nextForm.firstImage, nextForm.secondImage));
      setShowForm(true);
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to load blog details."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      window.alert("Please select an image file.");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setBlogImages((current) => [
        ...current,
        { id: String(Date.now()), value: dataUrl },
      ]);
    } catch (error) {
      window.alert(error.message || "Failed to load image.");
    } finally {
      event.target.value = "";
    }
  };

  const handleAddImageUrl = () => {
    const url = imageUrlInput.trim();

    if (!isValidImageUrl(url)) {
      window.alert("Enter a valid image URL starting with http:// or https://");
      return;
    }

    setBlogImages((current) => [
      ...current,
      { id: String(Date.now()), value: url },
    ]);
    setImageUrlInput("");
  };

  const removeBlogImage = (imageId) => {
    setBlogImages((current) => {
      const next = current.filter((image) => image.id !== imageId);
      return next.length
        ? next
        : normalizeImageList(emptyBlogForm.firstImage, emptyBlogForm.secondImage);
    });
  };

  const handleSaveBlog = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!blogForm.title.trim() || !blogForm.topic.trim() || !blogForm.desc.trim()) {
      window.alert("Topic, title, and description are required.");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        ...blogForm,
        firstImage:
          blogImages[0]?.value ||
          blogForm.firstImage ||
          "./assets/blog/blogsIOT.png",
        secondImage:
          blogImages[1]?.value ||
          blogForm.secondImage ||
          "./assets/blog/technology.png",
        answereList: blogForm.answereList.filter((item) => item.pointOne?.trim()),
      };

      if (editingBlogId) {
        await updateBlog(editingBlogId, payload);
      } else {
        await createBlog(payload);
      }

      setShowForm(false);
      resetForm();
      await loadBlogs();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to save blog."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!window.confirm("Delete this blog?")) {
      return;
    }

    try {
      await deleteBlog(blogId);
      await loadBlogs();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to delete blog."));
    }
  };

  const handleJsonUpload = async (event) => {
    if (!assertAdminAuth()) {
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsSaving(true);

    try {
      const fileText = await file.text();
      const parsed = JSON.parse(fileText);
      const items = Array.isArray(parsed) ? parsed : parsed.items;

      if (!Array.isArray(items) || !items.length) {
        window.alert("JSON must contain a non-empty array of blogs.");
        return;
      }

      await uploadBlogsJson(items);
      await loadBlogs();
      window.alert("Blogs uploaded successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to upload blogs JSON."));
    } finally {
      setIsSaving(false);
      event.target.value = "";
    }
  };

  const updateAnswerPoint = (index, value) => {
    setBlogForm((current) => ({
      ...current,
      answereList: current.answereList.map((item, itemIndex) =>
        itemIndex === index ? { ...item, pointOne: value } : item,
      ),
    }));
  };

  const addAnswerPoint = () => {
    setBlogForm((current) => ({
      ...current,
      answereList: [
        ...current.answereList,
        { id: String(current.answereList.length + 1), pointOne: "" },
      ],
    }));
  };

  if (showForm) {
    return (
      <div className="blog_Admin_Tab">
        <div className="upload_Section">
          <i
            className="icon-back backIcon cursorP"
            onClick={() => {
              setShowForm(false);
              resetForm();
            }}
          />
          <div className="img_Section blog_Image_Gallery">
            {blogImages.map((image) => (
              <div className="left_Section width50 blog_Image_Card" key={image.id}>
                <img
                  src={image.value}
                  className="blog_Img"
                  alt="Blog preview"
                />
                <button
                  type="button"
                  className="blog_Remove_Image"
                  onClick={() => removeBlogImage(image.id)}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="right_Section width50 blog_Add_Image_Card">
              <div
                className="add_Image cursorP"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="icon-add addIcon" />
                <span>{labels?.AddBlog?.imgPlaceholder || "Add more image"}</span>
              </div>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleImageFileChange}
              />
              <div className="blog_Image_Url_Row">
                <Form.Control
                  placeholder="Or paste https image URL"
                  value={imageUrlInput}
                  onChange={(event) => setImageUrlInput(event.target.value)}
                />
                <CustomButton title="Add URL" onClick={handleAddImageUrl} />
              </div>
            </div>
          </div>
          <div className="content_Section blog_Form">
            <Form.Control
              className="mb-3"
              placeholder="Topic"
              value={blogForm.topic}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  topic: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder={labels?.AddBlog?.heading || "Title of blog"}
              value={blogForm.title}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
            />
            <Form.Control
              as="textarea"
              className="mb-3 heightText"
              rows={4}
              placeholder={labels?.AddBlog?.subHeading || "Short description"}
              value={blogForm.desc}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  desc: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder="Image key (e.g. AutomationImg)"
              value={blogForm.img}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  img: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder="Paragraph heading"
              value={blogForm.paraHeading}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  paraHeading: event.target.value,
                }))
              }
            />
            <Form.Control
              as="textarea"
              className="mb-3 heightText"
              rows={3}
              placeholder={labels?.AddBlog?.description || "Full paragraph content"}
              value={blogForm.paraOne}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  paraOne: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder="Question one"
              value={blogForm.questionOne}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  questionOne: event.target.value,
                }))
              }
            />
            {blogForm.answereList.map((item, index) => (
              <Form.Control
                key={`answer-${index}`}
                className="mb-2"
                placeholder={`Answer point ${index + 1}`}
                value={item.pointOne}
                onChange={(event) => updateAnswerPoint(index, event.target.value)}
              />
            ))}
            <CustomButton title="Add Answer Point" onClick={addAnswerPoint} />
            <Form.Control
              className="mb-3 mt-3"
              placeholder="Question two"
              value={blogForm.questionTwo}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  questionTwo: event.target.value,
                }))
              }
            />
            <Form.Control
              as="textarea"
              className="mb-3 heightText"
              rows={3}
              placeholder="Answer paragraph"
              value={blogForm.answerPara}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  answerPara: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder="Button title"
              value={blogForm.btnTitle}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  btnTitle: event.target.value,
                }))
              }
            />
            <Form.Control
              className="mb-3"
              placeholder="Display date"
              value={blogForm.date}
              onChange={(event) =>
                setBlogForm((current) => ({
                  ...current,
                  date: event.target.value,
                }))
              }
            />
            <div className="upload_Btn">
              <CustomButton
                title={
                  isSaving
                    ? "Saving..."
                    : labels?.AddBlog?.updateBtn || "Upload Blog"
                }
                onClick={handleSaveBlog}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog_Admin_Tab">
      <div className="common_Header">
        <div className="search_Box">
          <SearchBox
            placeholder={labels?.searchPlaceHolder || "Search"}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <div className="right_Section">
          <div className="date_Inline">
            <div className="date_Section">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholderText={labels?.calendarStartPlaceHolder || "Start date"}
              />
              <i className="icon-calendar iconCalendar"></i>
            </div>
            <div className="date_Section marginR">
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                placeholderText={labels?.calendarEndPlaceHolder || "End date"}
              />
              <i className="icon-calendar iconCalendar"></i>
            </div>
          </div>
          <input
            ref={jsonInputRef}
            type="file"
            accept=".json,application/json"
            className="d-none"
            onChange={handleJsonUpload}
          />
          <CustomButton
            title={isSaving ? "Uploading..." : "Upload JSON"}
            onClick={() => jsonInputRef.current?.click()}
          />
          <CustomButton
            title={labels?.addBtn || "Add blog"}
            onClick={openCreateForm}
          />
        </div>
      </div>

      <div className="card_Section">
        {filteredBlogs.map((element) => (
          <div className="card_Main" key={element.id}>
            <div className="img_Section">
              <img
                src={getBlogImage(element, CardImg)}
                className="blog_Img displayDesktop"
                alt="..."
              />
              <img
                src={getBlogImage(element, IpadImg)}
                className="blog_Img displayIpad"
                alt="..."
              />
              <Dropdown className="custom_Dropdown">
                <Dropdown.Toggle variant="success" id={`blog-${element.id}`}>
                  <i className="icon-edit iconCss"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => openEditForm(element.id)}>
                    Edit blog
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="redC"
                    onClick={() => handleDeleteBlog(element.id)}
                  >
                    Delete blog
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <div className="date_Section">
                <span className="date">{element.date}</span>
              </div>
            </div>
            <div className="content_Section">
              <Label className="main_Heading" title={element.topic}></Label>
              <Label className="heading" title={element.title}></Label>
              <div className="inner_TxtSection">
                <Label className="inner_Txt" title={element.desc}></Label>
                <span className="read_More">{element.btnTitle}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogAdminTab;
